package org.zest.insan_360_website.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.browser.storage.IntStorageKey
import com.varabyte.kobweb.browser.storage.StorageKey
import com.varabyte.kobweb.browser.storage.getItem
import com.varabyte.kobweb.browser.storage.setItem
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.sessionStorage
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.zest.insan_360_website.components.widgets.ProgressButtonRow
import org.zest.insan_360_website.components.widgets.QuestionCell
import org.zest.insan_360_website.data.Language
import org.zest.insan_360_website.data.QUESTIONS
import org.zest.insan_360_website.data.page_states.AssessmentPageState
import org.zest.insan_360_website.data.page_states.UserPreferences
import org.zest.insan_360_website.model.Attribute

val Attribute.color: Color
    get() = when (this) {
        Attribute.Dominance -> Colors.Red
        Attribute.Influence -> Colors.DarkBlue
        Attribute.Steadiness -> Colors.Yellow.darkened(0.2F)
        Attribute.Compliance -> Colors.Green
        Attribute.Dummy -> Colors.Transparent
    }

class UserAnswersKey(name: String) : StorageKey<List<Answer>>(name) {
    override fun convertToString(value: List<Answer>): String = Json.encodeToString(value)
    override fun convertFromString(value: String): List<Answer>? =
        try {
            Json.decodeFromString(value)
        } catch (e: Exception) {
            null
        }
}

val USER_ANSWERS_KEY = UserAnswersKey("user_answers")
val USER_QUESTION_INDEX_KEY = IntStorageKey("user_question_index", 0)

data class Question(val statements: List<Statement>)

@Serializable
data class Statement(
    val en: String,
    val bm: String,
    val moreAttr: Attribute,
    val lessAttr: Attribute
) {
    val text: String
        get() = if (UserPreferences.language == Language.EN) en else bm
}

@Serializable
data class Answer(val moreStatement: Statement?, val lessStatement: Statement?) {
    fun isAnswered() = moreStatement != null && lessStatement != null
}

val FadeInSlideKeyframes = Keyframes {
    from {
        Modifier
            .opacity(0)
            .translateX(20.px)
    }
    to {
        Modifier
            .opacity(1)
            .translateX(0.px)
    }
}

val RightAngleMovementKeyframes = Keyframes {
    0.percent { Modifier.translateX(0.px) }
    50.percent { Modifier.translateX(5.px) }
    100.percent { Modifier.translateX(0.px) }
}

val LeftAngleMovementKeyframes = Keyframes {
    0.percent { Modifier.translateX(0.px) }
    50.percent { Modifier.translateX((-5).px) }
    100.percent { Modifier.translateX(0.px) }
}

val ButtonStyle = CssStyle {
    base {
        Modifier
            .fontSize(1.5.cssRem)
            .padding(leftRight = 0.px)
    }

    Breakpoint.LG {
        Modifier.fontSize(2.cssRem)
    }

    Breakpoint.XL {
        Modifier.fontSize(2.5.cssRem)
    }
}

val PageColumnStyle = CssStyle {
    base {
        Modifier.gap(0.5.cssRem)
    }

    Breakpoint.LG {
        Modifier.gap(1.cssRem)
    }

    Breakpoint.XL {
        Modifier.gap(2.cssRem)
    }
}

@Page
@Layout(".components.layouts.AssessmentPageLayout")
@Composable
fun AssessmentPage(ctx: PageContext) {

    val questionIndex = AssessmentPageState.index
    val questions = QUESTIONS
    val answers = AssessmentPageState.answers

    LaunchedEffect(Unit) {
        AssessmentPageState.index = sessionStorage.getItem(USER_QUESTION_INDEX_KEY) ?: 0
    }

    Column(
        modifier = PageColumnStyle.toModifier()
    ) {
        var isHoveredRight by remember { mutableStateOf(false) }
        var isHoveredLeft by remember { mutableStateOf(false) }

        QuestionCell(
            number = questionIndex + 1,
            question = questions[questionIndex],
            savedAnswer = answers[questionIndex],
            onAnswerChanged = { updatedAnswer ->
                AssessmentPageState.updateAnswer(questionIndex, updatedAnswer)
            }
        )
        ProgressButtonRow(
            questionIndex,
            isHoveredLeft,
            isHoveredRight,
            answers.all { it.isAnswered() },
            questions.size,
            { index ->
                AssessmentPageState.index = index
                sessionStorage.setItem(USER_QUESTION_INDEX_KEY, index)
            },
            { callback -> isHoveredLeft = callback },
            { callback -> isHoveredRight = callback },
            {
                ctx.router.navigateTo("/result", UpdateHistoryMode.REPLACE)
            }
        )
    }
}
